import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { LogOut, Sparkles, Cpu, Wand2, Loader2, Trash2, AlertTriangle, Globe, IndianRupee, DollarSign } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { BrandProfileCard } from "@/components/shared/BrandProfileCard";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

interface AiMode {
  mode: "claude" | "rules";
  updated_at: string | null;
  updated_by: string | null;
}

interface BillingCurrencyState {
  currency: "auto" | "USD" | "INR";
  effective_currency: "USD" | "INR";
  provider: "stripe" | "razorpay";
  updated_at: string | null;
  updated_by: string | null;
}

export const Settings: React.FC = () => {
  const { user, logout } = useAuth();
  const qc = useQueryClient();

  const initials = (user?.name || "U")
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");

  const aiMode = useQuery<AiMode>({
    queryKey: ["ai-mode"],
    queryFn: async () => (await api.get("/settings/ai-mode")).data,
  });

  const setMode = useMutation({
    mutationFn: async (mode: "claude" | "rules") =>
      (await api.put("/settings/ai-mode", { mode })).data as AiMode,
    onSuccess: (d) => {
      toast.success(
        d.mode === "claude"
          ? "Switched to Claude Sonnet 4.5 (uses Emergent credits)"
          : "Switched to Free Mode — rule-based classifier only"
      );
      qc.invalidateQueries({ queryKey: ["ai-mode"] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail || "Could not update AI mode"),
  });

  const isClaude = aiMode.data?.mode === "claude";

  return (
    <DashboardLayout title="Settings" subtitle="Manage your workspace and account." seoPath="/settings" seoDescription="Manage your SociBaba workspace, profile, security and platform connections.">
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="sg-card p-6 lg:col-span-2">
          <div className="sg-label mb-3">Profile</div>
          <div className="flex items-center gap-4">
            <Avatar className="h-14 w-14">
              {user?.picture && <AvatarImage src={user.picture} alt={user.name} />}
              <AvatarFallback className="bg-[color:var(--sg-fg)] text-white font-semibold">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="font-display text-xl font-semibold tracking-tight">
                {user?.name}
              </div>
              <div className="text-sm text-[color:var(--sg-muted)]">{user?.email}</div>
            </div>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="sg-label mb-1">Workspace</div>
              <div className="font-mono text-xs text-[color:var(--sg-muted)]">
                {user?.workspace_id}
              </div>
            </div>
            <div>
              <div className="sg-label mb-1">User ID</div>
              <div className="font-mono text-xs text-[color:var(--sg-muted)]">
                {user?.user_id}
              </div>
            </div>
          </div>
          <div className="mt-6">
            <Button
              data-testid="settings-logout"
              variant="outline"
              onClick={logout}
              className="border-[color:var(--sg-border)] hover:bg-[color:var(--sg-muted-bg)] hover:text-[color:var(--sg-fg)]"
            >
              <LogOut className="h-3.5 w-3.5 mr-1.5" /> Log out
            </Button>
          </div>
        </div>

        <div className="sg-card p-6 relative overflow-hidden">
          <div className="sg-grain" />
          <div className="relative">
            <div className="h-10 w-10 rounded-lg bg-[color:var(--sg-violet)]/10 flex items-center justify-center mb-3">
              <Sparkles className="h-5 w-5 text-[color:var(--sg-violet)]" />
            </div>
            <div className="font-display text-lg font-semibold tracking-tight">
              SociBaba · Signal
            </div>
            <p className="text-sm text-[color:var(--sg-muted)] mt-2 leading-relaxed">
              Every intent tag, complaint severity and reply draft is generated by AI, grounded in your product data. No hallucinated prices, no invented links.
            </p>
          </div>
        </div>
      </div>

      {/* AI classification mode — per workspace */}
      <div className="sg-card p-6 mt-6" data-testid="ai-mode-card">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="max-w-xl">
            <div className="sg-label mb-1">Comment classification</div>
            <div className="font-display text-lg font-semibold tracking-tight">
              AI mode for this workspace
            </div>
            <p className="text-sm text-[color:var(--sg-muted)] mt-2 leading-relaxed">
              Choose how SociBaba classifies incoming comments and drafts replies. The
              rule-based free mode keeps you on a <b>100% free API stack</b> (no Emergent
              credits used). Claude mode gives sharper intent detection and better-written
              replies, but consumes Emergent LLM credits.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="ai-mode-claude"
              disabled={setMode.isPending || isClaude}
              onClick={() => setMode.mutate("claude")}
              className={
                isClaude
                  ? "bg-[color:var(--sg-fg)] text-white hover:bg-black hover:text-white"
                  : "bg-white border border-[color:var(--sg-border)] text-[color:var(--sg-fg)] hover:bg-slate-50"
              }
            >
              {setMode.isPending && !isClaude ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <Wand2 className="h-3.5 w-3.5 mr-1.5" />
              )}
              Claude Sonnet 4.5
            </Button>
            <Button
              data-testid="ai-mode-rules"
              disabled={setMode.isPending || !isClaude}
              onClick={() => setMode.mutate("rules")}
              className={
                !isClaude
                  ? "bg-emerald-600 text-white hover:bg-emerald-700 hover:text-white"
                  : "bg-white border border-[color:var(--sg-border)] text-[color:var(--sg-fg)] hover:bg-slate-50"
              }
            >
              {setMode.isPending && isClaude ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <Cpu className="h-3.5 w-3.5 mr-1.5" />
              )}
              Free Mode (rules)
            </Button>
          </div>
        </div>

        <div className="mt-4 grid md:grid-cols-2 gap-3 text-xs">
          <div className={`border rounded-md p-3 ${isClaude ? "border-slate-400 bg-slate-50" : "border-[color:var(--sg-border)]"}`}>
            <div className="font-semibold flex items-center gap-1"><Wand2 className="h-3 w-3" /> Claude Sonnet 4.5</div>
            <ul className="text-[color:var(--sg-muted)] mt-1 space-y-0.5">
              <li>Nuanced intent + severity detection</li>
              <li>Human-quality reply drafts grounded in product FAQs</li>
              <li>Uses Emergent LLM credits (paid)</li>
            </ul>
          </div>
          <div className={`border rounded-md p-3 ${!isClaude ? "border-emerald-500 bg-emerald-50/60" : "border-[color:var(--sg-border)]"}`}>
            <div className="font-semibold flex items-center gap-1"><Cpu className="h-3 w-3" /> Free Mode</div>
            <ul className="text-[color:var(--sg-muted)] mt-1 space-y-0.5">
              <li>Keyword-based intent classification (~80% accuracy)</li>
              <li>Template reply drafts</li>
              <li><b>Zero cost</b> — 100% free API stack</li>
            </ul>
          </div>
        </div>

        {aiMode.data?.updated_at && (
          <div className="text-[11px] text-[color:var(--sg-muted)] mt-3">
            Last updated {new Date(aiMode.data.updated_at).toLocaleString()}
          </div>
        )}
      </div>

      <BillingCurrencyCard />

      <AutoTranslateCard />

      <BrandProfileCard />

      <DangerZone />
    </DashboardLayout>
  );
};


// ---------------------------- Billing currency ----------------------------

const BillingCurrencyCard: React.FC = () => {
  const qc = useQueryClient();
  const q = useQuery<BillingCurrencyState>({
    queryKey: ["billing-currency"],
    queryFn: async () => (await api.get("/settings/billing-currency")).data,
  });

  const setCur = useMutation({
    mutationFn: async (c: "auto" | "USD" | "INR") =>
      (await api.put("/settings/billing-currency", { currency: c })).data as BillingCurrencyState,
    onSuccess: (d) => {
      toast.success(
        d.currency === "auto"
          ? `Auto-detect enabled → currently billing in ${d.effective_currency} via ${d.provider === "razorpay" ? "Razorpay" : "Stripe"}.`
          : `Locked to ${d.currency} → routed through ${d.provider === "razorpay" ? "Razorpay" : "Stripe"}.`
      );
      qc.invalidateQueries({ queryKey: ["billing-currency"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Could not update currency"),
  });

  const current = q.data?.currency || "auto";

  const options: Array<{ value: "auto" | "USD" | "INR"; label: string; sub: string; icon: any }> = [
    { value: "auto", label: "Auto-detect", sub: "Based on your browser locale and location.", icon: Globe },
    { value: "INR", label: "INR (₹)", sub: "Bill in Indian Rupees via Razorpay — UPI, cards, netbanking.", icon: IndianRupee },
    { value: "USD", label: "USD ($)", sub: "Bill in US Dollars via Stripe — global cards, wallets.", icon: DollarSign },
  ];

  return (
    <div className="sg-card p-6 mt-6" data-testid="billing-currency-card">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="max-w-xl">
          <div className="sg-label mb-1">Billing</div>
          <div className="font-display text-lg font-semibold tracking-tight">
            Billing currency preference
          </div>
          <p className="text-sm text-[color:var(--sg-muted)] mt-2 leading-relaxed">
            Pick which currency (and payment gateway) SociBaba should charge you in.
            <b> Auto-detect</b> uses your browser locale — India → INR/Razorpay, everywhere else → USD/Stripe.
            Override this if, e.g., you&apos;re an Indian founder currently traveling abroad but want INR billing.
          </p>
        </div>
        <div className="text-right shrink-0">
          {q.data && (
            <>
              <div className="text-[10px] uppercase tracking-widest text-[color:var(--sg-muted)]">
                Currently billing in
              </div>
              <div className="font-display text-2xl font-bold tracking-tight text-[color:var(--sg-fg)]">
                {q.data.effective_currency}
              </div>
              <div className="text-[11px] text-[color:var(--sg-muted)]">
                via <b className="capitalize">{q.data.provider}</b>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="mt-5 grid md:grid-cols-3 gap-3" role="radiogroup" aria-label="Billing currency">
        {options.map((opt) => {
          const Icon = opt.icon;
          const active = current === opt.value;
          return (
            <button
              key={opt.value}
              data-testid={`billing-currency-${opt.value.toLowerCase()}`}
              role="radio"
              aria-checked={active}
              disabled={setCur.isPending || active}
              onClick={() => setCur.mutate(opt.value)}
              className={`text-left border rounded-lg p-4 transition ${
                active
                  ? "border-[color:var(--sg-fg)] bg-[color:var(--sg-fg)] text-white shadow-md"
                  : "border-[color:var(--sg-border)] bg-white hover:bg-slate-50"
              } disabled:cursor-default`}
            >
              <div className={`flex items-center gap-2 mb-1 ${active ? "text-white" : "text-[color:var(--sg-fg)]"}`}>
                <Icon className="h-4 w-4" strokeWidth={2} />
                <span className="font-semibold">{opt.label}</span>
                {active && (
                  <span className="ml-auto text-[10px] uppercase tracking-widest bg-white/20 px-2 py-0.5 rounded">
                    Active
                  </span>
                )}
              </div>
              <div className={`text-xs leading-snug ${active ? "text-white/85" : "text-[color:var(--sg-muted)]"}`}>
                {opt.sub}
              </div>
            </button>
          );
        })}
      </div>

      {q.data?.updated_at && (
        <div className="text-[11px] text-[color:var(--sg-muted)] mt-3">
          Last updated {new Date(q.data.updated_at).toLocaleString()}
        </div>
      )}
    </div>
  );
};


// ---------------------------- Auto-translate ----------------------------

interface AutoTranslateState {
  enabled: boolean;
  target_language: string;
  updated_at: string | null;
  updated_by: string | null;
}

const AUTO_TRANSLATE_LANGUAGES = [
  "English", "Hindi", "Tamil", "Telugu", "Bengali", "Marathi", "Gujarati",
  "Kannada", "Malayalam", "Punjabi", "Urdu", "Arabic", "Chinese (Simplified)",
  "Chinese (Traditional)", "Japanese", "Korean", "Spanish", "French", "German",
  "Portuguese", "Italian", "Russian", "Turkish", "Vietnamese", "Thai",
  "Indonesian", "Filipino",
];

const AutoTranslateCard: React.FC = () => {
  const qc = useQueryClient();
  const q = useQuery<AutoTranslateState>({
    queryKey: ["auto-translate"],
    queryFn: async () => (await api.get("/settings/auto-translate")).data,
  });

  const [target, setTarget] = React.useState<string>("");
  React.useEffect(() => {
    if (q.data?.target_language) setTarget(q.data.target_language);
  }, [q.data?.target_language]);

  const save = useMutation({
    mutationFn: async (payload: { enabled: boolean; target_language: string }) =>
      (await api.put("/settings/auto-translate", payload)).data as AutoTranslateState,
    onSuccess: (d) => {
      toast.success(
        d.enabled
          ? `Auto-translate ON — new comments will render in ${d.target_language}.`
          : "Auto-translate turned off. Only manual translations will run.",
      );
      qc.invalidateQueries({ queryKey: ["auto-translate"] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail || "Could not update auto-translate"),
  });

  const enabled = q.data?.enabled ?? false;
  const activeTarget = target || q.data?.target_language || "English";

  return (
    <div className="sg-card p-6 mt-6" data-testid="auto-translate-card">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="max-w-xl">
          <div className="sg-label mb-1">Comments</div>
          <div className="font-display text-lg font-semibold tracking-tight">
            Auto-translate incoming comments
          </div>
          <p className="text-sm text-[color:var(--sg-muted)] mt-2 leading-relaxed">
            When enabled, every new comment SociBaba ingests from your channels
            is auto-translated into your chosen language. The original text is
            always preserved — the translation appears underneath in the inbox.
            <b> Your subscription tier picks the best translator available (MyMemory on Free/Starter · Claude/Google/DeepL on Pro & Business).</b>
          </p>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--sg-muted)]">
            Status
          </div>
          <div className={`font-display text-2xl font-bold tracking-tight ${enabled ? "text-emerald-600" : "text-[color:var(--sg-muted)]"}`}>
            {enabled ? "ON" : "OFF"}
          </div>
          <div className="text-[11px] text-[color:var(--sg-muted)]">
            Target: <b>{q.data?.target_language || "English"}</b>
          </div>
        </div>
      </div>

      <div className="mt-5 grid md:grid-cols-2 gap-4 items-start">
        <div className="border border-[color:var(--sg-border)] rounded-lg p-4 bg-white">
          <div className="text-xs uppercase tracking-widest text-[color:var(--sg-muted)] mb-1.5">
            Translate to
          </div>
          <select
            value={activeTarget}
            onChange={(e) => setTarget(e.target.value)}
            data-testid="auto-translate-language"
            className="w-full h-9 px-2 rounded-md border border-[color:var(--sg-border)] bg-white text-sm"
          >
            {AUTO_TRANSLATE_LANGUAGES.map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
          <p className="text-[11px] text-[color:var(--sg-muted)] mt-2">
            Non-{activeTarget.toLowerCase()} comments will be translated on ingest.
            Same-language comments are left untouched.
          </p>
        </div>
        <div className="border border-[color:var(--sg-border)] rounded-lg p-4 bg-white flex flex-col gap-3">
          <div className="text-xs uppercase tracking-widest text-[color:var(--sg-muted)]">
            Actions
          </div>
          {enabled ? (
            <>
              <Button
                data-testid="auto-translate-save-target"
                disabled={save.isPending || (target === q.data?.target_language)}
                onClick={() => save.mutate({ enabled: true, target_language: activeTarget })}
                className="bg-[color:var(--sg-fg)] text-white hover:bg-black hover:text-white"
              >
                {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
                Save target language
              </Button>
              <Button
                variant="outline"
                data-testid="auto-translate-disable"
                disabled={save.isPending}
                onClick={() => save.mutate({ enabled: false, target_language: activeTarget })}
              >
                Turn off auto-translate
              </Button>
            </>
          ) : (
            <Button
              data-testid="auto-translate-enable"
              disabled={save.isPending}
              onClick={() => save.mutate({ enabled: true, target_language: activeTarget })}
              className="bg-emerald-600 text-white hover:bg-emerald-700 hover:text-white"
            >
              {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
              Enable auto-translate
            </Button>
          )}
        </div>
      </div>

      {q.data?.updated_at && (
        <div className="text-[11px] text-[color:var(--sg-muted)] mt-3">
          Last updated {new Date(q.data.updated_at).toLocaleString()}
        </div>
      )}
    </div>
  );
};



// ------------------------------- Danger zone -------------------------------

type ResetScope = "comments" | "leads" | "complaints" | "demo" | "synced_posts";

const RESET_ITEMS: { key: ResetScope; label: string; desc: string }[] = [
  { key: "comments", label: "Comments", desc: "All ingested comments across every platform." },
  { key: "leads", label: "Leads", desc: "All auto-derived buying-intent leads." },
  { key: "complaints", label: "Complaints", desc: "All auto-derived complaint records." },
  { key: "synced_posts", label: "Synced posts cache", desc: "Cached synced videos/posts. Re-syncing re-populates these." },
  { key: "demo", label: "Demo data", desc: "The 4 seeded sample products + mock (non-OAuth) platform accounts." },
];

const DangerZone: React.FC = () => {
  const [selected, setSelected] = React.useState<Record<ResetScope, boolean>>({
    comments: false, leads: false, complaints: false, demo: false, synced_posts: false,
  });
  const [confirm, setConfirm] = React.useState("");

  const mutation = useMutation({
    mutationFn: async () => {
      const params = new URLSearchParams();
      (Object.keys(selected) as ResetScope[]).forEach((k) => {
        if (selected[k]) params.set(k, "true");
      });
      const { data } = await api.post(`/cleanup/reset-workspace-data?${params.toString()}`);
      return data as { ok: boolean; deleted: Record<string, number> };
    },
    onSuccess: (d) => {
      const parts = Object.entries(d.deleted)
        .map(([k, v]) => `${v} ${k}`)
        .join(", ");
      toast.success(parts ? `Deleted: ${parts}` : "Nothing to delete.");
      setSelected({ comments: false, leads: false, complaints: false, demo: false, synced_posts: false });
      setConfirm("");
    },
    onError: (e: any) => toast.error("Reset failed: " + (e?.response?.data?.detail || e.message)),
  });

  const anySelected = Object.values(selected).some(Boolean);
  const canFire = anySelected && confirm.trim().toUpperCase() === "DELETE";

  return (
    <div
      data-testid="settings-danger-zone"
      className="mt-6 bg-white border-2 border-red-200 rounded-xl p-6 shadow-sm"
    >
      <div className="flex items-center gap-2 mb-1">
        <AlertTriangle className="h-4 w-4 text-red-600" />
        <h2 className="text-lg font-semibold text-red-700">Danger zone</h2>
      </div>
      <p className="text-sm text-[color:var(--sg-muted)] mb-4">
        Permanently delete data in your workspace. This cannot be undone.
      </p>

      <div className="space-y-2">
        {RESET_ITEMS.map((item) => (
          <label
            key={item.key}
            className="flex items-start gap-3 border border-[color:var(--sg-border)] rounded-md px-3 py-2.5 cursor-pointer hover:bg-slate-50"
          >
            <input
              type="checkbox"
              data-testid={`danger-scope-${item.key}`}
              checked={selected[item.key]}
              onChange={(e) => setSelected((s) => ({ ...s, [item.key]: e.target.checked }))}
              className="mt-0.5 h-4 w-4"
            />
            <div className="flex-1">
              <div className="text-sm font-medium text-[color:var(--sg-fg)]">{item.label}</div>
              <div className="text-xs text-[color:var(--sg-muted)]">{item.desc}</div>
            </div>
          </label>
        ))}
      </div>

      <div className="mt-4">
        <label className="text-xs font-medium text-[color:var(--sg-fg)]">
          Type <span className="font-mono bg-red-50 px-1.5 py-0.5 rounded text-red-700">DELETE</span> to confirm
        </label>
        <input
          data-testid="danger-confirm-input"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="DELETE"
          className="mt-1 w-full max-w-xs border border-[color:var(--sg-border)] rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-300"
        />
      </div>

      <div className="mt-4">
        <Button
          data-testid="danger-reset-button"
          disabled={!canFire || mutation.isPending}
          onClick={() => {
            if (window.confirm("Really delete the selected data? This cannot be undone.")) {
              mutation.mutate();
            }
          }}
          className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {mutation.isPending ? (
            <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
          ) : (
            <Trash2 className="h-3.5 w-3.5 mr-1.5" />
          )}
          Delete selected data
        </Button>
      </div>
    </div>
  );
};
